﻿/* global jQuery */
import { DesignerAppLauncher, BootstrapStatuses } from './DesignerAppLauncher.js';
import { BrowserEnvironment } from './BrowserEnvironment.js';
import { DesignerTelemetrySession } from './DesignerTelemetrySession.js';
import { DesignerAppService } from './DesignerAppService.js';
import { StartDesignerProcess } from './StartDesignerProcess.js';
import {
  EventIds,
  DownloadButtonStep,
  DownloadingStep,
  WaitingForInstallStep,
  InstallingStep,
  InstalledStep,
  StartButtonStep,
  StartOrDownloadStep,
  StartingStep,
  StartFailedTryDownloadStep,
  HelpGetAppLaunchedStep,
  AppLaunchedStep,
  UnexpectedErrorStep,
  FinalStep
} from './StartDesignerProcessSteps.js';

export class StartDesignerViewModel {
  constructor(config) {
    this.config = config;
    this.browserEnvironment = new BrowserEnvironment();

    // Declaring our state here helps seeing what state is available:
    this.sessionStorageMgr = null;
    this.designerAppService = null;
    this.adminService = null;
    this.telemetrySession = null;
    this.appLauncher = null;
    this.startDesignerProcess = null;
    this.clientDevice = null;
  }

  async setupAsync() {
    const config = this.config;
    // Do a quick analysis and if we are unable to run Designer, then let's just quit right now.
    const testResults = await this.analyzeClientDevice();
    if (config.hideStartButtonsIfCannotRun && !testResults.canDesignerRun) {
      const designerUnavailableBlock = jQuery('#' + config.dom.designerUnavailableBlockId);
      if (designerUnavailableBlock) {
        designerUnavailableBlock.show();
      }
      return;
    }

    const startDesignerBlock = jQuery('#' + config.dom.startDesignerBlockId);
    startDesignerBlock.show();

    /* The session storage service is a singleton within our langloStoreApp. Each time you request
     * a manager from it (with the same key), the same instance is returned. */
    const sessionStorageSvc = window.langloStoreApp.sessionStorageService;
    this.sessionStorageMgr = sessionStorageSvc.requestStorageManager('StartDesignerAction');
    this.designerAppService = new DesignerAppService(config.designerAppServiceUrl);
    this.adminService = config.adminService;

    this.telemetrySession = new DesignerTelemetrySession(this.designerAppService, this.sessionStorageMgr, /*isEnabled:*/ true);
    if (config.isPrimaryInstance) {
      //console.log(`Acquiring telemetry session...`);
      await this.telemetrySession.acquireSessionAsync('WebPageHit', 'StartDesignerActionBlock', this.clientDevice);
      //console.log(`Telemetry session acquired, session id: '${this.telemetrySession.sessionId}'`);
    }


    //--------- Designer App Launcher:

    const appLauncherConfig = {
      ...config.appLauncherConfig,
      isEndUser: config.isEndUser
    };
    this.appLauncher = new DesignerAppLauncher(this.designerAppService, this.adminService, this.sessionStorageMgr, this.telemetrySession, appLauncherConfig);
    this.appLauncher.deviceTestResults = testResults;
    // Delegate bootstrap monitoring events to the process object:
    this.appLauncher.onBootstrapStatusChanged = monitor => this.startDesignerProcess.notifyBootstrapStatusChanged(monitor);
    this.appLauncher.onBootstrapStatusMonitorError = (monitor, error) => this.startDesignerProcess.notifyBootstrapStatusMonitorError(monitor, error);


    //--------- Start Designer Process:

    const mainDom = config.dom;
    const processConfig = config.processConfig;

    const startDesignerBlockId = mainDom.startDesignerBlockId;
    const startDesignerBtnSelector = `#${startDesignerBlockId} .${mainDom.startDesignerBtnClass}`;
    const downloadDesignerBtnSelector = `#${startDesignerBlockId} .${mainDom.downloadDesignerBtnClass}`;
    processConfig.dialogConfig.popupSelector = `#${startDesignerBlockId} .designer-popup`;
    // Should instead be assigned in the start/download step classes?
    processConfig.dialogConfig.anchorSelector = `#${startDesignerBlockId} .${mainDom.startDesignerBtnClass}`;

    this.startDesignerProcess = new StartDesignerProcess(processConfig);
    this.setupStartDesignerProcess(startDesignerBtnSelector, downloadDesignerBtnSelector, testResults);


    //--------- Options Bar:

    const appIdSelector = document.getElementById(config.dom.selectAppIdId);
    if (appIdSelector) {
      appIdSelector.value = this.appLauncher.appId;
      appIdSelector.addEventListener('change', () => {
        this.appLauncher.appId = appIdSelector.value;
      });
    }
    const appCultureSelector = document.getElementById(config.dom.selectAppCultureId);
    if (appCultureSelector) {
      appCultureSelector.value = this.appLauncher.appCulture;
      appCultureSelector.addEventListener('change', async () => {
        this.appLauncher.appCulture = appCultureSelector.value;
      });
    }

    //---------------

    /* This next line was used for testing WebSocket real-time communication with a Windows application.
     * See the comment on top of the testWebSockets method for more. 1/14/2024. */
    //this.testWebSockets();
  }

  setupStartDesignerProcess(startDesignerBtnSelector, downloadDesignerBtnSelector, deviceTestResults) {
    const process = this.startDesignerProcess;

    // First, the two buttons which kick off the process. Note, we currently don't use the download button.

    if (!this.appLauncher.isBootstrapInstalled()) {
      const downloadBtnStep = process.addStep('Download-button', new DownloadButtonStep(this.telemetrySession, { downloadDesignerBtnSelector }));
      downloadBtnStep.isStartStep = true;
    }
    const startBtnStep = process.addStep('Start-button', new StartButtonStep(this.appLauncher, this.telemetrySession, { startDesignerBtnSelector }));
    startBtnStep.isStartStep = true;

    // Then, all the steps which are activated based on the rules engine declared further below:

    const downloadingStep = process.addStep('Downloading', new DownloadingStep(this.appLauncher));
    const waitForInstallStep = process.addStep('Wait-for-install', new WaitingForInstallStep(this.appLauncher, deviceTestResults));
    const installingStep = process.addStep('Installing', new InstallingStep());
    const installedStep = process.addStep('Installed', new InstalledStep());
    const startOrDownloadStep = process.addStep('Start-or-download', new StartOrDownloadStep(this.appLauncher, {
      startNowButtonText: this.config.startNowButtonText,
      downloadButtonText: this.config.downloadButtonText
    }));
    const startingStep = process.addStep('Starting', new StartingStep(this.appLauncher, {
      bootstrapLaunchWaitDuration: 5000,
      downloadButtonText: this.config.downloadButtonText,
      preventDesignerFromStarting: this.config.preventDesignerFromStarting,
      simulateDesignerNotStarting: this.config.simulateDesignerNotStarting
    }));
    const startFailedTryDownloadStep = process.addStep('Help-or-download', new StartFailedTryDownloadStep({
      downloadButtonText: this.config.downloadButtonText
    }));
    const helpGetAppLaunchedStep = process.addStep('Help-get-app-launched', new HelpGetAppLaunchedStep({
      downloadButtonText: this.config.downloadButtonText
    }));
    const appLaunchedStep = process.addStep('App-launched', new AppLaunchedStep(this.appLauncher));
    const unexpectedErrorStep = process.addStep('Unexpected-error', new UnexpectedErrorStep({
      useGlobalErrorHandler: true,
      copyErrorToClipboardBtnText: this.config.copyErrorToClipboardBtnText
    }));
    const finalStep = process.addStep('Final', new FinalStep(this.appLauncher));
    finalStep.isStopStep = true;

    // These are the rules which determine which steps are activated when:

    process.addRule(EventIds.appDownloadInitiated, downloadingStep);
    process.addRule(EventIds.appDownloaded, waitForInstallStep);
    process.addRule(EventIds.bootstrapStatusChanged, installingStep,
      (rule, event) => event.data.status === BootstrapStatuses.Installing);
    process.addRule(EventIds.bootstrapStatusChanged, installedStep,
      (rule, event) => event.data.status === BootstrapStatuses.Installed);

    process.addRule(EventIds.appStartInitiatedInstallUnknown, startOrDownloadStep);
    process.addRule(EventIds.appStartInitiated, startingStep);
    process.addRule(EventIds.gaveUpWaitingForBootstrapLaunch, startFailedTryDownloadStep);
    process.addRule(EventIds.needHelpGetAppLaunched, helpGetAppLaunchedStep);

    process.addRule(EventIds.bootstrapStatusChanged, appLaunchedStep,
      (rule, event) => event.data.status === BootstrapStatuses.AppLaunched);
    process.addRule(EventIds.errorOccurred, unexpectedErrorStep);
    process.addRule(EventIds.dialogClosed, finalStep);

    /* A Lucid Activity diagram can be generated using ChatGPT and the following Mermaid syntax
       (copy everything below this line and paste into ChatGPT):

    Create a new Lucid chart with the following Mermaid syntax:

    graph TD
      start --> downloadButton["Download button"]
      downloadButton -->|appDownloadInitiated| downloading["Downloading"]
      downloading -->|appDownloaded| waitForInstall["Wait for install"]
      waitForInstall --> hasDownloadStarted{"Did user start the install?"}
      hasDownloadStarted -->|Yes (bootstrapStatusChanged[Installing])| installing["Installing"]
      hasDownloadStarted -->|No| waitForInstall
      installing -->|bootstrapStatusChanged[Installed]| installed["Installed"]
      installed --> |bootstrapStatusChanged[AppLaunched]| appLaunched["ClickOnce Launched"]
      start --> startButton["Start button"]
      startButton --> isBootstrapInstalled{"Is Bootstrap app installed?"}
      isBootstrapInstalled -->|Yes (appStartInitiated)| startingStep
      isBootstrapInstalled -->|Unknown (appStartInitiatedInstallUnknown)| startOrDownloadStep
      startingStep --> didBootstrapStart{"Did the Bootstrap app start?"}
      didBootstrapStart -->|Yes (bootstrapStatusChanged[AppLaunched])| appLaunched
      didBootstrapStart -->|No (gaveUpWaitingForBootstrapLaunch)| startFailedTryDownloadStep["Start failed, try download Step"]
      didBootstrapStart -->|No (needHelpGetAppLaunched)| helpGetAppLaunchedStep["Help dismiss modal popup"]
      //didBootstrapStart -->|Download (appDownloadInitiated)| downloading["Downloading"]
      helpGetAppLaunchedStep --> wasPopupDismissed{"Was the modal popup dismissed?"}
      wasPopupDismissed -->|Yes, with "Open"| appLaunched
      wasPopupDismissed -->|Yes, with "Cancel"| failure
      wasPopupDismissed -->|No| failure
      startFailedTryDownloadStep --> didBootstrapStart // TODO Fix
      startOrDownloadStep --> chooseStartOrDownload{"Start app, or download?"}
      chooseStartOrDownload -->|Download (appDownloadInitiated)| downloading
      chooseStartOrDownload -->|Start appStartInitiated| startingStep
      appLaunched -->|dialogClosed| final
    */
  }

  async analyzeClientDevice() {
    this.clientDevice = this.browserEnvironment.analyzeClientDevice();
    if (this.config.simulateWrongOS) {
      console.warn('StartDesignerViewModel: simulating incompatible OS...');
      this.clientDevice.opSys = 'Unix';
    }
    const results = await this.browserEnvironment.analyzeAsync();
    /*
    testResults = {
      allRequirementsMet,   // True if oSRequirementOk or runtimeRequirementOk or browserRequirementOk
      oSRequirementOk,      // True if Windows XP or later
      runtimeRequirementOk, // True if .NET 4 runtime is installed
      browserRequirementOk, // True if the browser has the capability to start ClickOnce applications
      isIE,                 // Internet Explorer
      isGoogleChrome        // Google Chrome
      isFirefox,            // Firefox
      isEdge,               // Edge
      isClickOncePresent,   // True if the browser supports ClickOnce (e.g. if add-on has been installed)
    };
    */

    results.useBootstrapToStartApp = false
    if (results.allRequirementsMet) {
      results.canDesignerRun = true;
    } else if (!results.oSRequirementOk || !results.runtimeRequirementOk) {
      // If the OS is wrong or the .NET version is wrong, then we cannot run Designer
      results.canDesignerRun = false;
    } else if (results.browserRequirementOk) {
      // If browserRequirementOk is okay, it must be because the browser supports ClickOnce or starting Designer directly
      results.canDesignerRun = true;
    } else if (results.isGoogleChrome || results.isFirefox || results.isEdge) {
      // If we're on Chrome, Firefox, or Edge, then we can use our Bootstrap program to start Designer.
      results.canDesignerRun = true;
      // We can use our Bootstrap program on Chrome, Firefox, and Edge, on Windows:
      results.useBootstrapToStartApp = true;
    } else {
      results.canDesignerRun = false;
    }
    results.clientDevice = this.clientDevice;
    return results;
  }

  // Function to safely close the WebSocket connection
  closeWebSocket() {
    if (this.ws) {
      this.ws.close();
    }
  }

  /** This is the sample code for communicating with an external drawing service, i.e. the Langlo Designer Windows WPF application.
   *  The code was generated during a conversation with ChatGPT (see https://chat.openai.com/share/53b139da-9a96-4491-bc7e-4669402f29c0).
   *
   *  The code below acts as WebSocket client, talking to the service on the URL. The Designer
   *  application can then implement a WebSocket server for the URL. A demo application using
   *  the WebSocket-Sharp library (see https://github.com/sta/websocket-sharp) has been implemented
   *  and successfully tested with this code. See C:\Dev\Research\WebSocket\WpfTestApps\WebSocketSharpApp
   *  for more. */
  testWebSockets() {
    const ws = new WebSocket('ws://localhost:8080/DrawingService');
    this.ws = ws;

    ws.onopen = function () {
      console.log('WebSocket: Connection opened.');
      // Connection is open, send a command to edit the drawing
      console.log('WebSocket: Sending command...');
      ws.send(JSON.stringify({ command: 'edit', drawingId: 1234 }));
    };

    ws.onmessage = function (event) { // Received a message from the server...
      const message = JSON.parse(event.data);
      if (message.status === 'completed') {
        console.log(`WebSocket: received message, editing of drawing ${message.drawingId} has completed.`);
        // Perform any additional actions upon completion
      } else {
        console.log(`WebSocket: received unknown message with data: ${event.data}.`);
      }
    };

    ws.onerror = function (error) {
      console.error('WebSocket Error: ', error);
    };

    ws.onclose = function (event) {
      console.log('Connection closed', event); // WebSocket connection is closed
    };
  }
}
